/**
 * Reads the `config/affiliate_links` Firestore doc and exposes a hook
 * for building affiliate-tagged URLs.
 *
 * Source of truth lives server-side; web reads via a snapshot listener
 * so config changes (kill switch, tag rotation) propagate within
 * seconds without a hosting redeploy. Mirrors iOS
 * `AffiliateConfigService` and Android (forthcoming).
 *
 * Fail-open: if the read errors (rules, network, missing doc), URL
 * builders return the unmodified URL. The user always reaches the
 * retailer; we just don't earn attribution.
 *
 * See [[activate-amazon-associates-tag-zoeyorganizer-20-ac]] +
 * [[tasks/zoey-73]].
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";

export interface AffiliateEntry {
  tag: string | null;
  enabled: boolean;
  param: string;
}

export interface AffiliateConfig {
  /** True once the snapshot listener has fired at least once. */
  loaded: boolean;
  /** Per-store config keyed by canonical store id (`amazon`, `walmart`, etc.). */
  entries: Record<string, AffiliateEntry>;
}

const STORE_KEYS = ["amazon", "walmart", "kroger", "instacart", "target"] as const;

const EMPTY: AffiliateConfig = { loaded: false, entries: {} };

/**
 * Subscribes to `config/affiliate_links`. Returns the latest config.
 * Failures (offline, rules deny, doc missing) flip `loaded: true` with
 * empty entries so callers can stop spinning and still build URLs.
 */
export function useAffiliateConfig(): AffiliateConfig {
  const [config, setConfig] = useState<AffiliateConfig>(EMPTY);

  useEffect(() => {
    const ref = doc(db, "config", "affiliate_links");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = (snap.data() ?? {}) as Record<string, unknown>;
        const entries: Record<string, AffiliateEntry> = {};
        for (const key of STORE_KEYS) {
          const block = data[key];
          if (block && typeof block === "object") {
            const b = block as Record<string, unknown>;
            entries[key] = {
              tag: typeof b.tag === "string" ? b.tag : null,
              enabled: b.enabled === true,
              param: typeof b.param === "string" ? b.param : "",
            };
          }
        }
        setConfig({ loaded: true, entries });
      },
      () => setConfig({ loaded: true, entries: {} }),
    );
    return unsub;
  }, []);

  return config;
}

/**
 * Resolve the URL query item to append for a given canonical store id.
 * Returns null if the store is unconfigured / disabled / has no value.
 */
export function tagItemForStore(
  config: AffiliateConfig,
  storeId: string,
): { param: string; value: string } | null {
  const entry = config.entries[storeId];
  if (!entry || !entry.enabled || !entry.tag || !entry.param) return null;
  return { param: entry.param, value: entry.tag };
}

/**
 * Wraps a base retailer search URL with the affiliate tag if configured
 * + enabled. Returns the input URL unchanged when no tag applies.
 */
export function withAffiliateTag(
  baseUrl: string,
  storeId: string,
  config: AffiliateConfig,
): string {
  const item = tagItemForStore(config, storeId);
  if (!item) return baseUrl;
  const sep = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${sep}${encodeURIComponent(item.param)}=${encodeURIComponent(item.value)}`;
}

/**
 * True iff at least one retailer has an enabled tag in the loaded
 * config. Drives whether the in-app affiliate disclosure renders.
 * False before the config has loaded — disclosure stays hidden until
 * we know there's an active program to disclose.
 */
export function hasActiveAffiliateProgram(config: AffiliateConfig): boolean {
  return Object.values(config.entries).some(
    (e) => e.enabled && !!e.tag && !!e.param,
  );
}
